/*!
 * Copyright Optimus Ride 2020
 */

import { 
    AutocompletePrediction as GrpcAutocompletePrediction, 
    PlaceResult as GrpcPlaceResult,
    PlaceGeometry as GrpcPlaceGeometry,
    LatLngBounds as GrpcLatLngBounds,
    LatLngLiteral as GrpcLatLngLiteral,
    DirectionsRoute as GrpcDirectionsRoute,
    DirectionsLeg as GrpcDirectionsLeg,
    DirectionsStep as GrpcDirectionsStep,
    Distance as GrpcDistance,
    Duration as GrpcDuration
} from '@optimusride/client-library/grpc/places_pb';

interface GrpcLatLng {
    getLatitude: () => number;
    getLongitude: () => number;
}

// LatLngLiteral
export interface LatLngLiteral {
    lat: number;
    lng: number;
}

export function toLatLngLiteral(latlng: GrpcLatLngLiteral): LatLngLiteral {
    return (
        {
            lat:latlng.getLat(),
            lng:latlng.getLng()
        }
    )
}

export function isLatLngLiteral(thingToCheck:any):thingToCheck is AutocompletePrediction {
    return (
        thingToCheck &&
        (thingToCheck as LatLngLiteral).lat !== undefined &&
        (thingToCheck as LatLngLiteral).lng !== undefined
    );
}

// AutocompletePrediction
export interface AutocompletePrediction {
    description: string;
    place_id: string;
}

export function toAutocompletePrediction(prediction: GrpcAutocompletePrediction): AutocompletePrediction {
    return (
        {
            description: prediction.getDescription(),
            place_id: prediction.getPlaceId(),
        }
    )
}

export function isAutocompletePrediction(thingToCheck:any):thingToCheck is AutocompletePrediction {
    return (
        thingToCheck &&
        (thingToCheck as AutocompletePrediction).description !== undefined &&
        (thingToCheck as AutocompletePrediction).place_id !== undefined
    );
}

// LatLngBounds
interface LatLngBounds {
    northeast: LatLngLiteral;
    southwest: LatLngLiteral;
  }

export function toLatLngBounds(llb: GrpcLatLngBounds): LatLngBounds {
    return (
        {
            northeast:toLatLngLiteral((llb.getNortheast() as GrpcLatLngLiteral)),
            southwest:toLatLngLiteral((llb.getSouthwest() as GrpcLatLngLiteral)),
        }
    );
}

export function isLatLngBounds(thingToCheck:any):thingToCheck is LatLngBounds {
    return (
        thingToCheck &&
        isLatLngLiteral((thingToCheck as LatLngBounds).northeast) &&
        isLatLngLiteral((thingToCheck as LatLngBounds).southwest)
    );
}

// PlaceGeometry
export interface PlaceGeometry {
    location: LatLngLiteral;
    viewport: LatLngBounds;
}

export function toPlaceGeometry(pg: GrpcPlaceGeometry): PlaceGeometry {
    return (
        {
            location:toLatLngLiteral((pg.getLocation() as GrpcLatLngLiteral)),
            viewport:toLatLngBounds((pg.getViewport() as GrpcLatLngBounds)),
        }
    )
}

export function isPlaceGeometry(thingToCheck:any):thingToCheck is PlaceGeometry {
    return (
        thingToCheck &&
        typeof thingToCheck.location !== 'undefined' &&
        isLatLngLiteral(thingToCheck.location) &&
        typeof thingToCheck.viewport !== 'undefined' &&
        isLatLngBounds(thingToCheck.viewport)
    )
}

// PlaceResult
export interface PlaceResult {
    formattedAddress: string;
    geometry: PlaceGeometry;
    name: string;
    placeId: string;
}

export function toPlaceResult(pr: GrpcPlaceResult): PlaceResult {
    return (
        {
            formattedAddress: pr.getFormattedAddress(),
            geometry: toPlaceGeometry((pr.getGeometry() as GrpcPlaceGeometry)),
            name: pr.getName(),
            placeId: pr.getPlaceId()
        }
    )
}

export function isPlaceResult(thingToCheck:any):thingToCheck is PlaceResult {
    return (
        thingToCheck && typeof thingToCheck.formattedAddress === 'string' &&
        isPlaceGeometry(thingToCheck.geometry) &&
        typeof thingToCheck.name === 'string' &&
        typeof thingToCheck.placeId === 'string'
    )
 }
 
// LatLng
export interface LatLng {
    latitude: number,
    longitude: number
}

export function toLatLng(l: GrpcLatLng): LatLng {
    return (
        {
            latitude: l.getLatitude(),
            longitude: l.getLongitude()
        }
    )
}

export function isLatLng(thingToCheck:any):thingToCheck is LatLng {
    return (
            thingToCheck &&
            typeof thingToCheck.latitude === 'number' &&
            typeof thingToCheck.longitude === 'number'
    )
 }
 

// Distance
export interface Distance {
    text: string;
    value: number; // Distance in meters
}

export function toDistance(d: GrpcDistance): Distance {
    return (
        {
            text: d.getText(),
            value: d.getValue()
        }
    )
}

export function isDistance(thingToCheck:any):thingToCheck is Distance {
    return (
            thingToCheck &&
            typeof thingToCheck.text === 'string' &&
            typeof thingToCheck.value === 'number'
    )
 }
 

// Duration
export interface Duration {
    text: string;
    value: number; // Duration in seconds
}

export function toDuration(d: GrpcDuration): Duration {
    return (
        {
            text: d.getText(),
            value: d.getValue()
        }
    )
}

export function isDuration(thingToCheck:any):thingToCheck is Duration {
    return (
            thingToCheck &&
            typeof thingToCheck.text === 'string' &&
            typeof thingToCheck.value === 'number'
    )
 }
 

export enum TravelMode {
        BICYCLING,
        DRIVING,
        TRANSIT,
        TWO_WHEELER,
        WALKING,
    }

export interface DirectionsStep {
    distance: Distance;
    duration: Duration;
    end_location: LatLngLiteral;
    instructions: string;
    start_location: LatLngLiteral;
    travel_mode: TravelMode;
}

export function toDirectionsStep(bds: GrpcDirectionsStep): DirectionsStep {
    return (
        {
            distance: toDistance(bds.getDistance() as GrpcDistance),
            duration: toDuration((bds.getDuration() as GrpcDuration)),
            end_location: toLatLngLiteral((bds.getEndLocation() as GrpcLatLngLiteral)),
            instructions: bds.getInstructions(),
            start_location: toLatLngLiteral(bds.getStartLocation() as GrpcLatLngLiteral),
            travel_mode: bds.getTravelMode() as TravelMode
        }
    )
}

export interface DirectionsLeg {
    distance: Distance;
    duration: Duration;
    end_address: string;
    end_location: LatLngLiteral;
    start_address: string;
    start_location: LatLngLiteral;
}

export function toDirectionsLeg(bds: GrpcDirectionsLeg): DirectionsLeg {
    return (
        {
            distance: toDistance(bds.getDistance() as GrpcDistance),
            duration: toDuration((bds.getDuration() as GrpcDuration)),
            end_address: bds.getEndAddress(),
            end_location: toLatLngLiteral((bds.getEndLocation() as GrpcLatLngLiteral)),
            start_address: bds.getStartAddress(),
            start_location: toLatLngLiteral((bds.getStartLocation() as GrpcLatLngLiteral))
        }
    )
}

export function isDirectionsLeg(thingToCheck:any):thingToCheck is DirectionsLeg {
    return (
        thingToCheck &&
        isDistance(thingToCheck.distance) &&
        isDuration(thingToCheck.duration) &&
        typeof thingToCheck.end_address === 'string' &&
        isLatLngLiteral(thingToCheck.end_location) &&
        typeof thingToCheck.start_address === 'string' &&
        isLatLngLiteral(thingToCheck.start_location)
    )
 }
 

export interface DirectionsRoute {
    summary: string;
    bounds: LatLngBounds;
    legs: DirectionsLeg[];
    overview_polyline: string;
}

export function toDirectionsRoute(bds: GrpcDirectionsRoute): DirectionsRoute {
    return (
        {
            summary: bds.getSummary(),
            bounds: toLatLngBounds(bds.getBounds() as GrpcLatLngBounds),
            legs: bds.getLegsList().map(l=>toDirectionsLeg(l)),
            overview_polyline: bds.getOverviewPolyline()
        }
    )
}

